import { useState, useEffect, memo, useRef, useContext } from "react";
import RingCentralContext from "../../../RingCentralProvider";
import { Tabs, Row, Col, Space, message } from "antd";
import { Formik } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import SaleDropdown from "../../../components/SaleDropdown";
import { timerHistoryPayloadChange } from "../../../Slices/user";
import { settaskStartTime } from "../../../Slices/user";
import { Starttask } from "../../../Slices/timerslice";
import { updateWorkHistoryThunk } from "../../../Slices/orderSlice";
import { fetchLeadByIdThunk, getAllLeadWorkHistory, setRefreshStatus, setSale, setSalesWorkHistory, updateLeadThunk, setOpen as closeForm, fetchLeads } from "../../../Slices/salesSlice";
import { setLoader } from "../../../Slices/loaderSlice";
import { addLastContactedDate, setLastContactedDates } from "../../../Slices/lastContactedDatesSlice";
import { useErrorLogger } from "../../../utils";
import { normalizeTimeZone } from "../../../utils/timeZone";
import SaleInformation2 from "./SaleInformation2";
import CallingTrack from "./CallingTrack";
import EmailTrack from "./EmailTrack";
import LinkedinTrack from "./LinkedinTrack";
import SchedulingCall from "./SchedulingCall";
import OperationsTeam from "./OperationsTeam";
import { AntdButton } from "../../../blueplanitUtils/InputField";
import { useRecordLock } from "../../../hooks/useRecordLock";
import RecordLockedModal, { ReleaseRequestBanner } from "../../../components/RecordLockedModal";
import { dismissLeadReplyNotif } from "../../../Slices/leadReplyNotifsSlice";
import { fetchEmployeesByRoute } from "../../../Slices/Employeeaccessslice.js";
import WhatsAppRecordTab, { useCanSeeWhatsApp } from "../WhatsApp/WhatsAppRecordTab";

const validationSchema = Yup.object({
  agencyName: Yup.string()
    .required('Agency Name is required'),
  leadsContacts: Yup.array().of(
    Yup.object({
      email: Yup.string()
        .trim()
        .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9]{2,}$/, "Invalid email format")
        .email('Invalid email format'),
    })
  ),
  leadsScheduledCalls: Yup.array().of(
    Yup.object().shape({
      emails: Yup.string()
        .trim()
        .email("Invalid email format")
        .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9]{2,}$/, "Invalid email format")
    })
  ),
  leadsOPS: Yup.array().of(
    Yup.object().shape({
      emails: Yup.string()
        .trim()
        .email("Invalid email format") // Validate email format
        .matches(/^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z0-9]{2,}$/, "Invalid email format")
    })
  ),
});

const SaleDetails = memo((props) => {
  const { receivelastcalltime } = useContext(RingCentralContext);
  const [open, setOpen] = useState(false);
  const { id } = useParams();
  const dispatch = useDispatch();
  const { logError } = useErrorLogger();
  const navigate = useNavigate();
  const requestId = useSelector((state) => state.timer.requestId);
  // Route-filtered employees for Assign To dropdown (only employees with /sales route access)
  const routeFilteredEmployees = useSelector((state) => state.employeeAccess?.routeFilteredEmployees?.["/sales"] || []);
  const SalesInfo = useSelector((state) => state?.sales?.saleById?.[id] || state?.sales?.sale);
  const CallStatus = useSelector((state) => state?.sales?.callActive);
  const refreshStatus = useSelector((state) => state.sales.refreshStatus);
  const callingTrackRef = useRef();
  const [isRunning, setIsRunning] = useState(false);
  const [isDisabled, setIsDisabled] = useState(false);
  const [showSidePanel, setShowSidePanel] = useState(false);
  const [tabValue, setTabValue] = useState("0");
  const canSeeWhatsApp = useCanSeeWhatsApp();

  // Capture unread reply notifications for this lead, then dismiss them
  const pendingReplyNotifs = useSelector((state) =>
    (state.leadReplyNotifs?.notifications || []).filter(n => n.leadId === id)
  );
  const [replyBanner, setReplyBanner] = useState([]);
  useEffect(() => {
    if (!id) return;
    // Snapshot the notifications at open time so banner stays visible even after dismiss
    setReplyBanner(prev => {
      const incoming = (pendingReplyNotifs || []);
      if (incoming.length > 0 && prev.length === 0) return incoming;
      return prev;
    });
    if (pendingReplyNotifs.length > 0) {
      dispatch(dismissLeadReplyNotif(id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  useEffect(() => {
    dispatch(fetchEmployeesByRoute({ route: "/sales" }));
  }, [dispatch]);

  // Include current assignee in dropdown even if they no longer have route access (existing records untouched)
  const allEmployees = useSelector((state) => state.employee.AllEmployees);
  const assignToUsers = (() => {
    const filtered = [...routeFilteredEmployees];
    const currentAssignTo = SalesInfo?.assignTo;
    if (currentAssignTo && !filtered.some(e => `${e.firstname} ${e.lastname}` === currentAssignTo)) {
      const match = allEmployees?.find(e => `${e.firstname} ${e.lastname}` === currentAssignTo);
      if (match) filtered.push(match);
    }
    return filtered;
  })();
  const loginUser = useSelector((state) => state?.loginUserInfo?.loginUserDetails);
  const userId = loginUser?.id;
  const location = useLocation();
  const from = location.state?.from;

  const {
    isReadOnly: lockIsReadOnly,
    lockedBy,
    lockChecked,
    requestAccess,
    releaseRequested,
    releaseRequestFrom,
    dismissReleaseRequest,
    releaseForRequester,
  } = useRecordLock("leads", id);

  const [viewReadOnly, setViewReadOnly] = useState(false);
  const isReadOnly = lockIsReadOnly && viewReadOnly;


  // Move RC widget left when side panel opens so it's never hidden behind the panel
  useEffect(() => {
    const rcFrame = document.getElementById("rc-widget-adapter-frame");
    if (!rcFrame) return;
    if (showSidePanel) {
      rcFrame.style.setProperty("right", "50vw", "important");
    } else {
      rcFrame.style.removeProperty("right");
    }
  }, [showSidePanel]);

  useEffect(() => {
    if (!id) return;

    dispatch(setSale({}));
    dispatch(setLoader(true));

    dispatch(fetchLeadByIdThunk(id))
      .unwrap()
      .then((leadInfo) => {
        dispatch(addLastContactedDate(parseInt(leadInfo.lastContactedDate)));
      })
      .catch((error) => {
        console.error('Thunk failed:', error);
      })
      .finally(() => {
        dispatch(setLoader(false));
      });
  }, [dispatch, id]);

  const tabChange = (key) => {
    setTabValue(key);
  };

  const cancelHandelar = () => {
    if (from === "pipeline") {
      navigate("/Pipeline", { state: { from: "pipeline" } });
    } else {
      navigate("/sales", { state: { from: "sales" } });
    }
    dispatch(closeForm(false));
    dispatch(setLastContactedDates([]));

    // Re-fetch the leads list so filter-stale leads (e.g. follow-up date changed) are removed.
    // Pass isPipeline based on where the user came from (Rule 1 sync with Desktop fix).
    dispatch(setRefreshStatus(null));
    dispatch(fetchLeads({ isPipeline: from === "pipeline" }));

    dispatch(setSale({}));
    dispatch(setSalesWorkHistory([]));
  };

  const startTimer = () => {
    setIsDisabled(true); // Disable the button
    setTimeout(() => {
      setIsDisabled(false); // Enable the button after 5 seconds
    }, 5000);
  };
  const submit = async (values) => {
    const { lastUpdatedDate, ...newObject } =
      SalesInfo;
    const {
      lastUpdatedDate: last,
      ...createdObject
    } = values;
    try {
      if (JSON.stringify(newObject) === JSON.stringify(createdObject)) {
        startTimer();
        message.error({
          content: "No changes detected. Please update the fields before submitting.",
          duration: 3,
          className: 'custom-toast-center', // Custom warning styling
        });
        return;
      }

      startTimer();
      const valuesObject = {
        id: SalesInfo?.id,
        createdDate: values?.createdDate || '',
        createdBy: values?.createdBy || '',
        lastUpdatedDate: Date.now() + '' || '',
        lastContactedDate: values?.lastContactedDate || '',
        followup: values?.followup || false,
        followupDate: values?.followup ? (values?.followupDate || '') : null,
        callscheduledwithOPs: values?.callscheduledwithOPs || false,
        assign: values?.assign || false,
        assignedTo: values?.assignedTo || '',
        assignTo: values?.assignTo || '',
        isAssignLocked: values?.isAssignLocked || false,
        assignLockedAt: values?.assignLockedAt || null,
        assignUnlockAt: values?.assignUnlockAt || null,
        priority: values?.priority || '',
        status: values?.status || '',
        stage: values?.stage || '',
        channel: values?.channel || '',
        successfull: values?.successfull || false,
        agencyName: values?.agencyName || '',
        dba: values?.dba || '',
        address: values?.address || '',
        mailingAddress: values?.mailingAddress || '',
        city: values?.city || '',
        state: values?.state || '',
        timeZone: normalizeTimeZone(values?.timeZone) || '',
        webURL: values?.webURL || '',
        contactInfoURL: values?.contactInfoURL || '',
        linkedinURL: values?.linkedinURL || '',
        contactInfoComments: values?.contactInfoComments || '',
        callingComments: values?.callingComments || '',
        emailComments: values?.emailComments || '',
        linkedinComments: values?.linkedinComments || '',
        scheduledCallComments: values?.scheduledCallComments || '',
        opsTeamComments: values?.opsTeamComments || '',
        isInPipeline: values?.isInPipeline === true,
        pipelineStage: values?.pipelineStage || '',
        lostReason: values?.lostReason || '',
        dealValue: values?.dealValue || '',
        leadsContacts: values?.leadsContacts?.map(({ __typename, isStale, didYouMean, ...rest }) => ({
          ...rest
        })),
        leadsCalls: values?.leadsCalls?.map(({ __typename, ...rest }) => ({
          ...rest
        })),
        leadsEmails: values?.leadsEmails?.map(({ __typename, ...rest }) => ({
          ...rest
        })),
        leadsLinkedins: values?.leadsLinkedins?.map(({ __typename, ...rest }) => ({
          ...rest
        })),
        leadsScheduledCalls: values?.leadsScheduledCalls?.map(({ __typename, ...rest }) => ({
          ...rest
        })),
        leadsOPS: values?.leadsOPS?.map(({ __typename, ...rest }) => ({
          ...rest
        }))
      }
      dispatch(updateLeadThunk({ valuesObject, loginUser }))
        .then((res) => {
          receivelastcalltime(res?.payload?.leadsCalls?.[res.payload.leadsCalls.length - 1]?.date);
        });
    } catch (error) {

      await logError("SalesInformationPopup", "submit", error.message);

      console.error("Error updating lead:", error);

      message.error({
        content: "Failed to update lead. Please try again.",
        duration: 5,
        className: "alert alert-danger text-center", // Custom error styling
      });
    }
  };

  const handleOpen = () => {
    setOpen(true);
  };

  const handleStop = async () => {
    setIsRunning(false);
    const valuesObject = {
      id: Math.random().toString(),
      userId: userId,
      title: "",
      description: "",
      clientName: "Blueplanit",
      department: "Sales",
      taskType: "Available",
      startTime: Date.now().toString(),
      endTime: Date.now().toString(),
      requestId: "",
    };
    dispatch(timerHistoryPayloadChange({ data: valuesObject }));

    const values = {
      userId: userId,
      emailId: "",
      requestId: "",
      clientName: "Blueplanit",
      processType: "",
      taskType: "Available",
      email: "",
      subject: "",
      body: "",
      startTime: Date.now().toString(),
      endTime: "",
      department: "Sales"

    }
    await dispatch(updateWorkHistoryThunk(values))
      .unwrap()
      .then(() => {
        setIsRunning(false);
      })
    const payload = {
      task: "Available",
      time: Date.now(),
      department: "",
      requestId: ''
    };
    localStorage.setItem("time", new Date().getTime());
    localStorage.removeItem("department");
    localStorage.removeItem("requestId");
    localStorage.removeItem("task");
    dispatch(settaskStartTime(new Date().getTime()));
    dispatch(Starttask(payload));
    dispatch(getAllLeadWorkHistory(SalesInfo?.id));
  };
  const handleDropdownClose = () => {
    setOpen(false);
  };
  return (
    <>
      {/* Record locked by another user */}
      {lockChecked && lockIsReadOnly && !viewReadOnly && (
        <RecordLockedModal
          visible={true}
          lockedBy={lockedBy}
          onViewReadOnly={() => setViewReadOnly(true)}
          onRequestAccess={requestAccess}
          releaseRequested={releaseRequested}
          onGoBack={() => {
            if (from === "pipeline") navigate("/Pipeline");
            else navigate("/sales");
          }}
        />
      )}

      {/* Banner for lock holder when someone requests access */}
      {releaseRequestFrom && (
        <ReleaseRequestBanner
          requestFrom={releaseRequestFrom}
          onRelease={() => {
            releaseForRequester();
            if (from === "pipeline") navigate("/Pipeline");
            else navigate("/sales");
          }}
          onDismiss={dismissReleaseRequest}
        />
      )}

    <div className="page-wrapper">
      <div className="content container-fluid">
        {/* New email reply banner */}
        {replyBanner.length > 0 && (
          <div style={{
            background: 'linear-gradient(135deg, #f5f3ff, #ede9fe)',
            border: '1px solid #c4b5fd', borderRadius: 8, marginBottom: 10,
            padding: '10px 16px', display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <span style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg, #8b5cf6, #6366f1)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 14, color: '#fff',
            }}>↩</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 13, color: '#4c1d95' }}>
                {replyBanner.length === 1
                  ? `New email reply from ${replyBanner[0].from || 'contact'}`
                  : `${replyBanner.length} new email replies received`}
              </div>
              {replyBanner[0]?.subject && (
                <div style={{ fontSize: 11, color: '#6d28d9', marginTop: 2 }}>
                  Re: {replyBanner[0].subject}
                </div>
              )}
            </div>
            <button
              onClick={() => setReplyBanner([])}
              style={{
                background: 'none', border: 'none', color: '#7c3aed',
                fontSize: 18, cursor: 'pointer', lineHeight: 1, padding: '0 4px',
              }}
            >×</button>
          </div>
        )}

        {/* Read-only banner */}
        {isReadOnly && (
          <div style={{
            background: "#fff7e6", border: "1px solid #ffd591", padding: "8px 16px",
            borderRadius: 6, marginBottom: 8, display: "flex", alignItems: "center", gap: 8,
          }}>
            <span style={{ fontSize: 16 }}>🔒</span>
            <span>
              <strong>Read-Only Mode</strong> — This lead is being edited by{" "}
              <strong>{lockedBy?.userName}</strong>. You can view but not modify.
            </span>
          </div>
        )}

        <div style={{ width: "100%" }}>
          {Object.keys(SalesInfo).length > 0 && (
            <Formik
              initialValues={SalesInfo}
              onSubmit={(values) => { if (!isReadOnly) submit(values); }}
              validationSchema={validationSchema}
              validateOnChange={true}
              enableReinitialize={true}
              key={SalesInfo?.id}
            >
              {({
                handleChange,
                handleBlur,
                handleSubmit,
                isSubmitting,
                values,
                setFieldValue,
                errors,
                touched,
                setFieldTouched
              }) => (
                <div style={isReadOnly ? { pointerEvents: "none", opacity: 0.7 } : {}}>
                  <form noValidate onSubmit={handleSubmit}>
                    <Row gutter={16}>
                      <Col
                        span={24}
                        style={{
                          marginTop: "-20px",
                          justifyContent: "space-between",
                          display: "flex",
                          ...(isReadOnly ? { pointerEvents: "auto" } : {}),
                        }}
                        align="end"
                      >
                        <Space>
                          {!isReadOnly && (
                            <AntdButton
                              color="primary"
                              variant="solid"
                              htmlType="submit"
                              disabled={isDisabled}
                              loading={CallStatus}
                              size="small"
                              label="Update"
                              style={{ marginTop: '10px', marginBottom: '10px' }}
                            />
                          )}

                          <AntdButton
                            color="danger"
                            variant="solid"
                            onClick={cancelHandelar}
                            label="Close"
                            size="small"
                            style={isReadOnly ? { pointerEvents: "auto" } : {}}
                          />
                        </Space>
                        {isRunning || Number(requestId) === SalesInfo?.leadID ? (
                          <AntdButton
                            color="danger"
                            variant="solid"
                            size="small"
                            label="Stop"
                            onClick={handleStop}
                            style={{
                              float: "right",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                            }}
                          />
                        ) : (
                          <AntdButton
                            color='primary' variant='solid'
                            size="small"
                            style={{
                              float: "right",
                              display: "flex",
                              justifyContent: "center",
                              alignItems: "center",
                              marginRight: '20px',
                              marginTop: '5px'
                            }}
                            onClick={handleOpen}
                            label="Start"
                          />
                        )}

                      </Col>
                      <div
                        onClick={() => setShowSidePanel(!showSidePanel)}
                        className={`sidepanel-toggle ${showSidePanel ? "open" : ""}`}
                      >
                        <i className="fa-solid fa-bars"></i>
                      </div>
                    </Row>

                    {open && (
                      <SaleDropdown
                        open={open}
                        handleClose={handleDropdownClose}
                        handleStart={handleOpen}
                        SalesInfo={SalesInfo}
                      />
                    )}
                    <SaleInformation2
                      SalesInfo={SalesInfo}
                      setFieldValue={setFieldValue}
                      assignToUsers={assignToUsers}
                      id={id}
                      callingTrackRef={callingTrackRef}
                      showSidePanel={showSidePanel}
                    />

                    <div
                      className="sale-side-panel"
                      style={{
                        position: "fixed",
                        top: 98,
                        right: showSidePanel ? "0px" : "-50%",
                        width: "49vw",
                        height: "calc(100vh - 98px)",
                        backgroundColor: "#fff",
                        boxShadow: "-2px 0px 8px rgba(0,0,0,0.2)",
                        transition: "right 0.35s ease-in-out",
                        padding: "15px",
                        zIndex: 200,
                        overflowY: "auto",
                      }}
                    >
                      <Tabs
                        activeKey={String(tabValue)}
                        onChange={tabChange}
                        tabPosition="top"
                        items={[
                          {
                            key: "0",
                            label: (
                              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <i className="fa-solid fa-phone" />
                              </span>
                            ),
                          },
                          {
                            key: "1",
                            label: (
                              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <i className="fa-solid fa-envelope" />
                              </span>
                            ),
                          },
                          {
                            key: "2",
                            label: (
                              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <i className="fa-brands fa-linkedin" />
                              </span>
                            ),
                          },
                          {
                            key: "3",
                            label: (
                              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <i className="fa-solid fa-calendar-check" />
                              </span>
                            ),
                          },

                          {
                            key: "4",
                            label: (
                              <span style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <i className="fa-solid fa-people-group" />
                              </span>
                            ),
                          },
                          ...(canSeeWhatsApp
                            ? [{
                                key: "5",
                                label: (
                                  <span style={{ display: "flex", alignItems: "center", gap: 6 }} title="WhatsApp">
                                    <i className="fa-brands fa-whatsapp" />
                                  </span>
                                ),
                              }]
                            : []),
                        ]}
                      />

                      {/* All track components stay mounted — only visibility toggles.
                          This prevents data loss when switching tabs during calls or editing. */}
                      <div style={{ display: tabValue === "0" ? "block" : "none" }}>
                        <CallingTrack
                          SalesInfo={SalesInfo}
                          assignToUsers={assignToUsers}
                          setFieldValue={setFieldValue}
                          id={id}
                          ref={callingTrackRef}
                          leadID={SalesInfo?.leadID}
                        />
                      </div>
                      <div style={{ display: tabValue === "1" ? "block" : "none" }}>
                        <EmailTrack
                          SalesInfo={SalesInfo}
                          assignToUsers={assignToUsers}
                          setFieldValue={setFieldValue}
                          id={id}
                          leadID={SalesInfo?.leadID}
                        />
                      </div>
                      <div style={{ display: tabValue === "2" ? "block" : "none" }}>
                        <LinkedinTrack
                          SalesInfo={SalesInfo}
                          assignToUsers={assignToUsers}
                          setFieldValue={setFieldValue}
                          id={id}
                          leadID={SalesInfo?.leadID}
                        />
                      </div>
                      <div style={{ display: tabValue === "3" ? "block" : "none" }}>
                        <SchedulingCall
                          SalesInfo={SalesInfo}
                          assignToUsers={assignToUsers}
                          setFieldValue={setFieldValue}
                          id={id}
                        />
                      </div>
                      <div style={{ display: tabValue === "4" ? "block" : "none" }}>
                        <OperationsTeam
                          SalesInfo={SalesInfo}
                          assignToUsers={assignToUsers}
                          setFieldValue={setFieldValue}
                          id={id}
                        />
                      </div>
                      {canSeeWhatsApp && (
                        <div style={{ display: tabValue === "5" ? "block" : "none" }}>
                          <WhatsAppRecordTab leadId={id} recordName={SalesInfo?.agencyName} active={tabValue === "5"} />
                        </div>
                      )}
                    </div>
                  </form>
                </div>
              )}
            </Formik>
          )}
        </div>
      </div>
    </div>
    </>
  );
});

export default SaleDetails;